import React, { useCallback, useEffect, useState } from "react";
import { Boxes, Cloud, HardDrive, MapPinned, Navigation2, Radio } from "lucide-react";
import GlobalFeedback from "../../../global/GlobalFeedback";
import {
  getStorageSettings,
  updateStorageSettings,
  updateGeofencingSettings,
} from "../api/storageSettingsApi";
import "../styles/SyncStoragePanel.css";

const OPTIONS = [
  {
    id: "local",
    title: "Local",
    desc: "Disk under /assets",
    hint: "This server",
    Icon: HardDrive,
  },
  {
    id: "supabase",
    title: "Supabase",
    desc: "Managed online buckets",
    hint: "Cloud",
    Icon: Cloud,
  },
  {
    id: "s3",
    title: "AWS S3",
    desc: "Your Amazon bucket",
    hint: "Object store",
    Icon: Boxes,
  },
];

export default function SyncStoragePanel() {
  const [provider, setProvider] = useState("local");
  const [envDefault, setEnvDefault] = useState("local");
  const [geofencingEnabled, setGeofencingEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingGeofence, setSavingGeofence] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getStorageSettings();
      const data = res?.data || {};
      setProvider(data.provider || "local");
      setEnvDefault(data.envDefault || "local");
      setGeofencingEnabled(
        data.geofencingEnabled != null
          ? Boolean(data.geofencingEnabled)
          : data.geofencing?.enabled != null
            ? Boolean(data.geofencing.enabled)
            : true,
      );
    } catch (err) {
      setError(err?.message || "Failed to load storage settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onSelect = async (next) => {
    if (next === provider || saving) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await updateStorageSettings(next);
      const data = res?.data || {};
      setProvider(data.provider || next);
      setSuccess(res?.message || `Storage switched to ${next}`);
    } catch (err) {
      setError(err?.message || "Failed to switch storage");
    } finally {
      setSaving(false);
    }
  };

  const setGeofencing = async (next) => {
    if (savingGeofence || next === geofencingEnabled) return;
    setSavingGeofence(true);
    setError("");
    setSuccess("");
    try {
      const res = await updateGeofencingSettings(next);
      const data = res?.data || {};
      setGeofencingEnabled(
        data.geofencingEnabled != null
          ? Boolean(data.geofencingEnabled)
          : Boolean(data.geofencing?.enabled),
      );
      setSuccess(
        res?.message ||
          (next
            ? "Geofencing enabled — staff must mark attendance inside premises."
            : "Geofencing disabled — staff can mark attendance from any location."),
      );
    } catch (err) {
      setError(err?.message || "Failed to update geofencing");
    } finally {
      setSavingGeofence(false);
    }
  };

  const activeOpt = OPTIONS.find((o) => o.id === provider) || OPTIONS[0];
  const ActiveIcon = activeOpt.Icon;

  return (
    <div className="sync-storage">
      <div className="sync-storage__mesh" aria-hidden />

      <header className="sync-storage__head">
        <div className="sync-storage__head-main">
          <p className="sync-storage__eyebrow">System</p>
          <h2 className="sync-storage__title">Sync &amp; Storage</h2>
          <p className="sync-storage__sub">
            Route every new upload, then set how far staff can be when they punch in.
          </p>
        </div>
        {!loading ? (
          <div className="sync-storage__signals" aria-label="Current routing">
            <div className="sync-storage__signal">
              <span className="sync-storage__signal-label">Upload path</span>
              <strong>
                <ActiveIcon size={14} strokeWidth={2.2} aria-hidden />
                {activeOpt.title}
              </strong>
            </div>
            <div
              className={`sync-storage__signal${geofencingEnabled ? " is-bound" : " is-free"}`}
            >
              <span className="sync-storage__signal-label">Attendance</span>
              <strong>
                {geofencingEnabled ? (
                  <MapPinned size={14} strokeWidth={2.2} aria-hidden />
                ) : (
                  <Navigation2 size={14} strokeWidth={2.2} aria-hidden />
                )}
                {geofencingEnabled ? "Bound" : "Open"}
              </strong>
            </div>
          </div>
        ) : null}
      </header>

      <GlobalFeedback type="error" text={error} />
      <GlobalFeedback type="success" text={success} />

      {loading ? (
        <div className="sync-storage__loading" aria-busy="true">
          <span className="sync-storage__loading-bar" />
          <p>Loading routing console…</p>
        </div>
      ) : (
        <div className="sync-storage__stack">
          <section className="sync-storage__route" aria-labelledby="sync-storage-provider-title">
            <div className="sync-storage__route-top">
              <div>
                <h3 id="sync-storage-provider-title">Upload destination</h3>
                <p>
                  New files only · env default <em>{envDefault}</em>
                </p>
              </div>
              <div className="sync-storage__origin" aria-hidden>
                <Radio size={16} strokeWidth={2} />
                <span>New upload</span>
                <span className="sync-storage__origin-beam" />
              </div>
            </div>

            <div
              className={`sync-storage__destinations sync-storage__destinations--${provider}`}
              role="radiogroup"
              aria-label="Storage provider"
            >
              {OPTIONS.map((opt, index) => {
                const selected = provider === opt.id;
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`sync-storage__dest sync-storage__dest--${opt.id}${selected ? " is-live" : ""}`}
                    style={{ "--ss-i": index }}
                    disabled={saving}
                    onClick={() => onSelect(opt.id)}
                  >
                    <span className="sync-storage__dest-glow" aria-hidden />
                    <span className="sync-storage__dest-icon" aria-hidden>
                      <Icon size={26} strokeWidth={1.7} />
                    </span>
                    <span className="sync-storage__dest-meta">
                      <span className="sync-storage__dest-hint">{opt.hint}</span>
                      <span className="sync-storage__dest-title">{opt.title}</span>
                      <span className="sync-storage__dest-desc">{opt.desc}</span>
                    </span>
                    <span className={`sync-storage__dest-state${selected ? " is-on" : ""}`}>
                      {selected ? "Receiving" : "Route here"}
                    </span>
                  </button>
                );
              })}
            </div>

            {saving ? <p className="sync-storage__status">Rerouting uploads…</p> : null}
          </section>

          <section
            className={`sync-storage__reach${geofencingEnabled ? " is-bound" : " is-free"}`}
            aria-labelledby="sync-geofence-title"
          >
            <div className="sync-storage__reach-stripe" aria-hidden />

            <div className="sync-storage__reach-main">
              <span
                className={`sync-storage__reach-icon${geofencingEnabled ? " is-bound" : " is-free"}`}
                aria-hidden
              >
                {geofencingEnabled ? (
                  <MapPinned size={18} strokeWidth={2} />
                ) : (
                  <Navigation2 size={18} strokeWidth={2} />
                )}
              </span>

              <div className="sync-storage__reach-copy">
                <div className="sync-storage__reach-title-row">
                  <h3 id="sync-geofence-title">Attendance reach</h3>
                  <span
                    className={`sync-storage__reach-badge${geofencingEnabled ? " is-bound" : " is-free"}`}
                  >
                    {geofencingEnabled ? "Geofenced" : "Unrestricted"}
                  </span>
                </div>
                <p>
                  {geofencingEnabled
                    ? "Staff must be inside a premises radius to mark attendance."
                    : "Staff can mark attendance from any location."}
                </p>
              </div>

              <div
                className="sync-storage__stripe-toggle"
                role="radiogroup"
                aria-label="Attendance geofencing"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={geofencingEnabled}
                  className={`sync-storage__stripe-opt${geofencingEnabled ? " is-active" : ""}`}
                  disabled={savingGeofence}
                  onClick={() => setGeofencing(true)}
                >
                  On premises
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={!geofencingEnabled}
                  className={`sync-storage__stripe-opt${!geofencingEnabled ? " is-active" : ""}`}
                  disabled={savingGeofence}
                  onClick={() => setGeofencing(false)}
                >
                  Anywhere
                </button>
              </div>
            </div>

            {savingGeofence ? (
              <p className="sync-storage__status sync-storage__status--reach">
                Updating attendance reach…
              </p>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
